import { LocationModel } from './location.model';
import { BaseModel } from './base.model';
import { Deserializable } from './deserializable';

export enum AppointmentType {
  Holiday = 'holiday',
  Hiringday = 'hiringday',
  AvailabilityModerator = 'availability_moderator',
  AvailabilityObserver = 'availability_observer',
}

export enum AppointmentDataDaytype {
  Allday = 'allday',
  Morning = 'morning',
  Afternoon = 'afternoon',
}

export interface AppointmentDataType {
  type?: AppointmentDataDaytype;
  locations?: Array<number> | null;
  holiday?: {
    name: string;
    states: Array<string> | null;
  };
}

export class AppointmentModel extends BaseModel implements Deserializable {

  // columns
  owner_id: number | null;
  type: AppointmentType;
  day: string;
  data: AppointmentDataType | null;
  status: string;
  used: number | null;
  used_by_ac?: any | null;

  // relations
  mother?: AppointmentModel;
  children?: Array<AppointmentModel>;
  acs?: Array<any>;

  public isHoliday(): boolean {
    return this.type === AppointmentType.Holiday;
  }

  public isHiringday(): boolean {
    return this.type === AppointmentType.Hiringday;
  }

  public getHolidayName(): string | null {
    return (this.isHoliday() && this.data?.holiday?.name) ? this.data.holiday.name : null;
  }

  public getHolidayStates(): Array<string> | null {
    return (this.isHoliday() && this.data?.holiday?.states) ? this.data.holiday.states.map((stateName: string) => {
      return (this.transloco) ? this.transloco.translate('common.states.' + stateName) : stateName;
    }) : null;
  }

  public hasAcs(): boolean {
    return Array.isArray(this.acs) && this.acs.length > 0;
  }

  public getTooltipText(): string | null {
    let text = null;
    if (this.hasAcs()) {
      text = this.acs.map(ac => ac.name).join(', ');
    } else if (this.isHoliday()) {
      text = this.getHolidayName();
      const states = this.getHolidayStates();
      if (text !== null && states?.length > 0 && states?.length < 16) {
        text += '\n\n' + states.join('\n');
      }
    } else if (this.isHiringday()) {
      text = this.transloco.translate('common.labels.hiringday');
    } else if (this.isAllday()) {
      text = this.transloco.translate('common.labels.available_allday');
    } else if (this.isMorning()) {
      text = this.transloco.translate('common.labels.available_morning');
    } else if (this.isAfternoon()) {
      text = this.transloco.translate('common.labels.available_afternoon');
    }
    return text || null;
  }

  public getPayloadForSaving(): any {
    return {
      id: this.id,
      type: this.type,
      day: new Date(this.day).toISOString().split('T')[0], // @todo verbessern
      data: this.data
    };
  }

  public setAllday(): void {
    this.initData();
    delete this.data.type;
  }

  public setMorning(): void {
    this.initData();
    this.data.type = AppointmentDataDaytype.Morning;
  }

  public setAfternoon(): void {
    this.initData();
    this.data.type = AppointmentDataDaytype.Afternoon;
  }

  public getDayType(): AppointmentDataDaytype | null {
    return this.data?.type || null;
  }

  public setDayType(daytype: AppointmentDataDaytype): void {
    if (!this.data) {
      this.initData();
    }
    this.data.type = daytype;
  }

  public isAllday(): boolean {
    return !this.data?.type || this.data?.type === AppointmentDataDaytype.Allday;
  }

  public isMorning(): boolean {
    return this.data?.type === AppointmentDataDaytype.Morning;
  }

  public isAfternoon(): boolean {
    return this.data?.type === AppointmentDataDaytype.Afternoon;
  }

  public isLocalHoliday(): boolean {
    return (this.getHolidayStates()?.length > 0 && this.getHolidayStates()?.length < 16);
  }

  public isNationalHoliday(): boolean {
    return this.type === AppointmentType.Holiday && !this.isLocalHoliday();
  }

  public getLocations(): Array<number> | null {
    return this.data?.locations || null;
  }

  public setLocations(locations: Array<number> | null): void {
    if (!this.data) {
      this.initData();
    }
    if (!this.data?.locations) {
      this.data.locations = [];
    }
    this.data.locations = locations;
  }

  public setHolidayData(name: string, states: Array<string> | null): void {
    if (!this.data) {
      this.initData();
    }
    if (!this.data?.holiday) {
      this.data.holiday = {
        name: '',
        states: []
      };
    }
    this.data.holiday.name = name;
    this.data.holiday.states = states;
  }

  public getDataList(locations?: Array<LocationModel>): string {
    const list: Array<string> = [];
    if (this.isMorning()) {
      list.push(this.transloco.translate('common.labels.morning'));
    }
    if (this.isAfternoon()) {
      list.push(this.transloco.translate('common.labels.afternoon'));
    }
    const mylocations: Array<number> | null = this.getLocations();
    if (mylocations !== null && Array.isArray(mylocations)) {
      if (Array.isArray(locations)) {
        const names: Array<string> = [];
        locations.forEach((location: LocationModel) => {
          if (mylocations.find(mylocation => mylocation === location.id)) {
            names.push(location.name);
          }
        });
        if (names.length > 0) {
          list.push(this.transloco.translate('common.labels.only') + ' ' + names.join(', '));
        }
      } else {
        list.push(mylocations.join(', '));
      }
    }
    return list.join(' / ');
  }

  private initData(): void {
    if (this.data === undefined || this.data === null) {
      this.data = {};
    }
  }

}
